import { GridFilterInputValue, GridFilterItem, GridFilterOperator } from '@mui/x-data-grid';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

interface EntityOption {
    id?: string | null;
    name?: string;
}

export interface EntityFilterInputProps<T extends EntityOption> {
    item: GridFilterItem;
    applyValue: (item: GridFilterItem) => void;
    options: T[];
}

export const EntityFilterInput = <T extends EntityOption>({
    item,
    applyValue,
    options,
}: EntityFilterInputProps<T>) => {
    const selected = options.find((option) => option.id === item.value) ?? null;

    return (
        <Autocomplete
            options={options}
            getOptionLabel={(option) => option.name ?? ''}
            value={selected}
            onChange={(_, newValue) => applyValue({ ...item, value: newValue?.id ?? '' })}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => <TextField {...params} variant="standard" label="Value" />}
            size="small"
            sx={{ minWidth: 200 }}
        />
    );
};

const createEntityValueFilterOperator = (
    label: string,
    value: string,
    InputComponent: GridFilterOperator['InputComponent'],
): GridFilterOperator => ({
    label,
    value,
    getApplyFilterFn: (filterItem) => {
        if (!filterItem.value) {
            return null;
        }
        return () => true;
    },
    InputComponent,
});

const createEntityNoValueFilterOperator = (label: string, value: string): GridFilterOperator => ({
    label,
    value,
    getApplyFilterFn: () => () => true,
});

export const createEntityFilterOperators = (
    InputComponent: GridFilterOperator['InputComponent'],
): GridFilterOperator[] => [
    createEntityValueFilterOperator('equals', 'equals', InputComponent),
    createEntityValueFilterOperator('not equal', 'notEquals', InputComponent),
    createEntityValueFilterOperator('contains', 'contains', GridFilterInputValue),
    createEntityNoValueFilterOperator('is null', 'isNull'),
    createEntityNoValueFilterOperator('is not null', 'isNotNull'),
];
